import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { debounce } from "es-toolkit/function"
import { fetchQuery } from "relay-runtime"
import { SearchInput, SuggestionSearch } from "@/components/ui/search"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import {
  MAX_MEDICATION_SEARCH_CHARACTERS,
  MIN_MEDICATION_SEARCH_CHARACTERS,
} from "@/modules/patients/detail/medication/types/medication-config"
import { DeMedicationFragmentInlineList } from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { getRecommendedMedications } from "@/modules/patients/detail/progress-note/lib/is-recommended-medications-empty"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { RecommendedMedicationResult } from "@/modules/patients/detail/recommended-medications/components/recommended-medication-result"
import {
  useDeMedicationSearchQuery,
  useDeMedicationSearchQuery$data,
} from "@/modules/patients/services/de/__generated__/useDeMedicationSearchQuery.graphql"
import { deMedicationSearchQuery } from "@/modules/patients/services/de/useDeMedicationSearchQuery"
import { RelayEnvironment } from "@/relay-environment"

interface DeRecommendedMedicationsSearchState {
  isLoading?: boolean
  searchResultStatus: SearchResultStatus
  de?: {
    preparations?: NonNullable<useDeMedicationSearchQuery$data["de_preparations"]["result"]>
    substances?: NonNullable<useDeMedicationSearchQuery$data["de_substances"]["result"]>
  }
  renderOptions?: { title: string; options: DeMedicationFragmentInlineList }[]
  hasOptions?: boolean
  error?: Error
}

type FormattedOptions = { title: string; options: DeMedicationFragmentInlineList }[]

type SearchResultStatus = "default" | "noResults" | "hasResults"

const initializeState = (options: FormattedOptions): DeRecommendedMedicationsSearchState => {
  const hasOptions = options.some((groupOption) => groupOption.options.length > 0)

  return { hasOptions, renderOptions: options, searchResultStatus: hasOptions ? "hasResults" : "default" }
}

const useFormatExistingMedications = (progressNote: ProgressNoteData) => {
  const { activeMedications, inactiveMedications } = useMedications<DeMedicationFragmentInlineList>()

  const medications = getRecommendedMedications(progressNote)

  return [
    {
      title: "patient.medication.active",
      options: activeMedications.filter(
        (f) => !medications.some((medication) => medication.node.medication.id === f.id),
      ),
    },
    {
      title: "patient.medication.inactive",
      options: inactiveMedications.filter(
        (f) => !medications.some((medication) => medication.node.medication.id === f.id),
      ),
    },
  ]
}

export const DeRecommendedMedicationsSearch = (props: {
  progressNote: ProgressNoteData
  onCloseMenu: VoidFunction
}) => {
  const existingMedications = useFormatExistingMedications(props.progressNote)
  const [result, setResult] = useState<DeRecommendedMedicationsSearchState>(() => initializeState(existingMedications))

  const inputRef = useRef<HTMLInputElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const previousSearchRef = useRef("")
  const [inputValue, setInputValue] = useState<string>("")

  const { t } = useTranslation()

  const handleClear = () => {
    setResult(() => initializeState(existingMedications))
    setInputValue("")
  }

  const handleSearchInternal = async (event: React.ChangeEvent<HTMLInputElement>) => {
    previousSearchRef.current = event.target.value.trim()
    if (previousSearchRef.current.length < MIN_MEDICATION_SEARCH_CHARACTERS) {
      setResult(() => initializeState(existingMedications))
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setResult((prev) => ({ ...prev, isLoading: true, isDisplayNotExistingMedication: false }))

    fetchQuery<useDeMedicationSearchQuery>(
      RelayEnvironment,
      deMedicationSearchQuery,
      { searchTerm: previousSearchRef.current },
      { fetchPolicy: "store-or-network" },
    ).subscribe({
      error(error: Error) {
        setResult((prev) => ({ ...prev, error, isLoading: false }))
      },
      next(response) {
        const { de_preparations, de_substances } = response
        const dePreparationsResult = de_preparations?.result
        const deSubstancesResult = de_substances?.result

        setResult((prev) => {
          const latest: DeRecommendedMedicationsSearchState = {
            ...prev,
            isLoading: false,
            searchResultStatus:
              dePreparationsResult && deSubstancesResult && deSubstancesResult.length > 0 ? "hasResults" : "noResults",
            de: { preparations: dePreparationsResult, substances: deSubstancesResult },
          }
          return latest
        })
      },
    })
  }

  const debounceSearch = useRef(debounce(handleSearchInternal, 500)).current

  return (
    <SuggestionSearch>
      <div className="p-2">
        <SearchInput
          ref={inputRef}
          isLoading={result.isLoading}
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value.trim())
            debounceSearch(event)
          }}
          placeholder={t("common.search")}
          autoComplete="off"
          maxLength={MAX_MEDICATION_SEARCH_CHARACTERS}
          onClear={handleClear}
          dataTestId="recommended-medication"
        />
      </div>
      <RecommendedMedicationResult
        onCloseMenu={props.onCloseMenu}
        inputValue={inputValue}
        searchResultStatus={result.searchResultStatus}
        renderOptions={result.renderOptions}
        progressNoteId={props.progressNote.id}
        preparations={{ type: "DE", preparations: result.de?.preparations }}
        substances={{ type: "DE", substances: result.de?.substances }}
      />
    </SuggestionSearch>
  )
}
